import { useEffect, useState } from "react";
import * as yup from "yup";
import axios from "../../../../services/axios";
import Constants from "../../../../constants/Index";
import { useOnboarding } from "../../../../hooks/useOnboarding";
import { ButtonV2, DropdownV2, formatErrorMessageV2, FormCardV2, FormGridV2, InputV2, OptionV2, RemoveButtonV2 } from "../../../../components/FormControlsV2";
import { OnboardingFooterV2 } from "../../../../components/OnboardingV2/OnboardingFooterV2";
import { generateuuid } from "../../../../utils/uuid/generate";
import { hasErrors, validateV2, ValidationErrorsV2 } from "../adapters/validation";
import { persistPayload } from "../adapters/payload";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";

type ProfileFormV2 = {
  id?: string | number;
  uuid?: string;
  profile: OptionV2 | null;
  name: string;
  phone: string;
};

const baseProfile: ProfileFormV2 = {
  profile: null,
  name: "",
  phone: "",
};

const profileSchema = yup.object().shape({
  users: yup.array().of(
    yup.object().shape({
      profile: yup.object().nullable().required(() => i18n.t("internalValidation.selectRole")),
      name: yup
        .string()
        .matches(/^[^\s]+(\s+[^\s]+)*$/, () => i18n.t("internalValidation.validName"))
        .min(3, () => i18n.t("internalValidation.minName"))
        .required(() => i18n.t("internalValidation.required")),
      phone: yup.string().min(11, () => i18n.t("internalValidation.invalidPhone")).max(11, () => i18n.t("internalValidation.invalidPhone")).required(() => i18n.t("internalValidation.required")),
    })
  ),
});

export function CreateProfilesV2() {
  const { t } = useTranslation();
  const { payload, setPayload, setBlockNextStep, apiFieldErrors, setApiFieldErrors, clearStepError } = useOnboarding();
  const [profilesList, setProfilesList] = useState<OptionV2[]>([]);
  const [phoneUseds, setPhoneUseds] = useState<string[]>([]);
  const [users, setUsers] = useState<ProfileFormV2[]>([baseProfile]);
  const [errors, setErrors] = useState<ValidationErrorsV2>({});
  const [showFullValidation, setShowFullValidation] = useState(false);

  function updateUser(index: number, partial: Partial<ProfileFormV2>) {
    const nextUsers = users.map((user, userIndex) => (userIndex === index ? { ...user, ...partial } : user));
    setUsers(nextUsers);
    validateUsers(nextUsers);
  }

  function addUser() {
    const nextUsers = [...users, { ...baseProfile }];
    setUsers(nextUsers);
    validateUsers(nextUsers);
  }

  function removeUser(index: number) {
    const removedUserId = getUserIdentifier(users[index]);
    const nextUsers = users.length > 1 ? users.filter((_, userIndex) => userIndex !== index) : users;
    setUsers(nextUsers);
    syncUsersAndClearRemovedReferences(nextUsers, removedUserId);
    validateUsers(nextUsers);
  }

  function toPayloadUsers(usersToPayload: ProfileFormV2[]) {
    return usersToPayload.map((user) => ({
      ...user,
      uuid: user.uuid || generateuuid(),
      password: Constants.DEFAULT_PASSWORD,
      profile_id: user.profile?.value,
    }));
  }

  function getUserIdentifier(user?: ProfileFormV2 | any) {
    return user?.uuid || user?.id || "";
  }

  function isSameUserReference(value: any, userId: string | number) {
    if (!userId) return false;
    if (value && typeof value === "object" && "value" in value) return String(value.value) === String(userId);
    return String(value) === String(userId);
  }

  function clearManagerReference(item: Record<string, any>, userId: string | number) {
    const next = { ...item };
    if (isSameUserReference(next.manager_user_id, userId)) next.manager_user_id = null;
    if (isSameUserReference(next.owner_user_id, userId)) next.owner_user_id = null;
    return next;
  }

  function syncUsersAndClearRemovedReferences(nextUsers: ProfileFormV2[], userId: string | number) {
    setPayload((current) => {
      const next = {
        ...current,
        users: toPayloadUsers(nextUsers),
        farms: userId ? (current?.farms || []).map((farm: any) => clearManagerReference(farm, userId)) : current?.farms,
        areas: userId ? (current?.areas || []).map((area: any) => clearManagerReference(area, userId)) : current?.areas,
      };
      persistPayload(next);
      return next as any;
    });
  }

  async function validateUsers(usersToValidate = users, fullValidation = showFullValidation) {
    const validation = fullValidation ? await validateV2(profileSchema, { users: usersToValidate }) : {};
    const phoneCounter = new Map<string, number>();
    const selectedProfiles = usersToValidate.map((user) => String(user.profile?.value || ""));
    const hasManager = selectedProfiles.includes(String(Constants.PROFILE_CODE_GERENTE));
    const hasForeman = selectedProfiles.includes(String(Constants.PROFILE_CODE_CAPATAZ));

    usersToValidate.forEach((user) => {
      if (user.phone) phoneCounter.set(user.phone, (phoneCounter.get(user.phone) || 0) + 1);
    });

    if (fullValidation && (!hasManager || !hasForeman)) {
      validation.profiles = !hasManager && !hasForeman
        ? "Cadastre pelo menos um perfil Gerente e um perfil Capataz."
        : !hasManager
          ? "Cadastre pelo menos um perfil Gerente."
          : "Cadastre pelo menos um perfil Capataz.";
    }

    usersToValidate.forEach((user, index) => {
      if (user.phone && phoneCounter.get(user.phone)! > 1) validation[`users[${index}].phone`] = t("internalValidation.phoneInUse");
      if (user.phone && phoneUseds.includes(user.phone)) validation[`users[${index}].phone`] = "Telefone já está sendo utilizado.";
    });

    setErrors(validation);
    const invalid = hasErrors(validation);
    setBlockNextStep(invalid);
    if (!invalid) {
      clearStepError(1, 0);
      setApiFieldErrors((current) =>
        Object.fromEntries(Object.entries(current).filter(([field]) => !field.startsWith("users[")))
      );
    }
    return !invalid;
  }

  async function handleNext() {
    setShowFullValidation(true);
    const isValid = await validateUsers(users, true);
    if (!isValid) return false;

    const payloadUsers = toPayloadUsers(users);

    setPayload((current) => {
      const next = { ...current, users: payloadUsers };
      persistPayload(next);
      return next as any;
    });
    return true;
  }

  function mapApiUserField(field: string) {
    const fieldName = field.replace(/^users\[\d+\]\./, "");
    const fieldMap = {
      profileId: "profile",
      profile_id: "profile",
    } as Record<string, string>;
    return fieldMap[fieldName] || fieldName;
  }

  useEffect(() => {
    axios.get("/profiles").then(({ data }) => {
      setProfilesList(data.map((profile: any) => ({ label: profile.name, value: profile.id })));
    });
    axios.get("/users/user").then(({ data }) => {
      setPhoneUseds(data.map((user: any) => user.phone));
    });
  }, []);

  useEffect(() => {
    if (payload?.users?.length) {
      setUsers(
        payload.users.map((user: any) => ({
          id: user.id,
          uuid: user.uuid,
          profile:
            user.profile ||
            profilesList.find((profile) => String(profile.value) === String(user.profile_id)) ||
            null,
          name: user.name || "",
          phone: user.phone || "",
        }))
      );
    }
  }, [payload, profilesList]);

  useEffect(() => {
    const apiErrors = Object.entries(apiFieldErrors).reduce((nextErrors, [field, message]) => {
      const match = field.match(/^users\[(\d+)\]\.(.+)$/);
      if (!match) return nextErrors;
      nextErrors[`users[${match[1]}].${mapApiUserField(field)}`] = message;
      return nextErrors;
    }, {} as ValidationErrorsV2);

    if (hasErrors(apiErrors)) setErrors((current) => ({ ...current, ...apiErrors }));
  }, [apiFieldErrors]);

  useEffect(() => {
    setBlockNextStep(false);
  }, [setBlockNextStep]);

  return (
    <>
      <main>
        <div className="onboarding-v2-form-page">
          <FormCardV2
            title={t("formCards.profiles")}
            description={
              <>
                {t("formCards.profilesHelp")}
                <br />
                {t("formCards.requiredProfilesHelp")}
              </>
            }
          >
            {errors.profiles && (
              <small className="ov2-field__error animation__fadeIn">{formatErrorMessageV2(errors.profiles)}</small>
            )}
            {users.map((user, index) => (
              <div className="onboarding-v2-list-card" key={user.uuid || index}>
                <FormCardV2
                  title={t("formCards.profileNumber", { number: index + 1 })}
                  actions={
                    users.length > 1 ? (
                      <RemoveButtonV2 onClick={() => removeUser(index)} />
                    ) : undefined
                  }
                >
                  <FormGridV2>
                    <DropdownV2
                      name={`users[${index}].profile`}
                      label={t("onboardingRemaining.role")}
                      value={user.profile}
                      options={profilesList}
                      searchable
                      error={errors[`users[${index}].profile`]}
                      onBlur={() => validateUsers()}
                      onChange={(profile) => updateUser(index, { profile })}
                    />
                    <InputV2
                      name={`users[${index}].name`}
                      label={t("onboardingRemaining.name")}
                      value={user.name}
                      error={errors[`users[${index}].name`]}
                      onBlur={() => validateUsers()}
                      onChange={(name) => updateUser(index, { name })}
                    />
                    <InputV2
                      name={`users[${index}].phone`}
                      label={t("onboardingRemaining.phone")}
                      value={user.phone}
                      type="tel"
                      mask="phone"
                      error={errors[`users[${index}].phone`]}
                      onBlur={() => validateUsers()}
                      onChange={(phone) => updateUser(index, { phone })}
                    />
                  </FormGridV2>
                </FormCardV2>
              </div>
            ))}
            <div className="onboarding-v2-page-actions">
              <ButtonV2 onClick={addUser}>+ ADICIONAR PERFIL</ButtonV2>
            </div>
          </FormCardV2>
        </div>
      </main>
      <OnboardingFooterV2 nextStepEvent={handleNext} />
    </>
  );
}
